import React, { useState, useMemo, useCallback, memo } from 'react';
import { BarChart3, TrendingUp, Clock, AlertTriangle, CheckCircle, Search } from 'lucide-react';
import KPICard from 'components/common/KPICard';
import DataTable from 'components/common/DataTable';
import CustomPieChart from 'components/common/CustomPieChart';
import ClassificacaoOSSDashboard from './ClassificacaoOSSDashboard';
import GraficosTempoDashboard from './GraficosTempoDashboard';
import SearchableFilterSelect from 'components/common/SearchableFilterSelect';

const OSSEstoqueDashboard = memo(({ data, loading }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [busca, setBusca] = useState('');
  const [filtroCidade, setFiltroCidade] = useState('');
  const [filtroTecnico, setFiltroTecnico] = useState('');
  const [buscaNaoFinalizada, setBuscaNaoFinalizada] = useState('');
  const [filtroCidadeNaoFinalizada, setFiltroCidadeNaoFinalizada] = useState('');
  const [filtroTecnicoNaoFinalizada, setFiltroTecnicoNaoFinalizada] = useState('');

  const obterCampo = (item, campo) => (
    item?.[campo]
    ?? item?.[campo?.toLowerCase()]
    ?? item?.[campo?.charAt(0) + campo?.slice(1).toLowerCase()]
    ?? item?.[campo?.charAt(0).toUpperCase() + campo?.slice(1).toLowerCase()]
  );

  const agregarPorCampo = useCallback((lista = [], campo, campoValor = 'TOTAL', limite = 10, campoId = 'ID') => {
    const agrupados = {};

    lista.forEach((item) => {
      const chaveBruta = obterCampo(item, campo) ?? 'N/A';
      const chave = String(chaveBruta || 'N/A').trim() || 'N/A';
      const idBruto = obterCampo(item, campoId);
      const id = idBruto !== undefined && idBruto !== null && String(idBruto).trim() !== ''
        ? String(idBruto).trim()
        : null;
      const valor = Number(obterCampo(item, campoValor) ?? 1);
      const valorSeguro = Number.isFinite(valor) ? valor : 0;
      if (!agrupados[chave]) {
        agrupados[chave] = {
          nome: chave,
          ids: new Set(),
          valor: 0
        };
      }

      if (id) {
        agrupados[chave].ids.add(id);
      }
      agrupados[chave].valor += valorSeguro;
    });

    return Object.values(agrupados)
      .map(({ ids, nome, valor }) => {
        const idsList = Array.from(ids || []);
        const idUnico = idsList.length === 1 ? idsList[0] : null;
        return {
          name: idUnico ? `${idUnico} - ${nome}` : nome,
          value: valor
        };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, limite);
  }, []);

  // Calcular KPIs adicionais
  const kpisCalculados = useMemo(() => {
    // Extrair dados dentro do useMemo para evitar dependências externas
    const kpis = data.kpis || {};
    const naoFinalizada = data.nao_finalizada?.oss || [];
    
    const totalOSS = kpis.TOTAL || 0;
    const mediaPorDia = parseFloat(kpis.media_por_dia || 0);
    const ossNaoFinalizadas = naoFinalizada.length;
    const percentualNaoFinalizadas = totalOSS > 0 ? ((ossNaoFinalizadas / totalOSS) * 100).toFixed(1) : 0;
    
    return {
      totalOSS,
      mediaPorDia,
      ossNaoFinalizadas,
      percentualNaoFinalizadas
    };
  }, [data]);

  // Dados para gráficos de pizza
  const dadosDiagnostico = useMemo(() => {
    const porDiagnostico = data.por_diagnostico?.oss || [];
    return agregarPorCampo(porDiagnostico, 'DIAGNOSTICO', 'TOTAL', 10, 'ID');
  }, [data, agregarPorCampo]);

  // Extrair listas únicas para filtros
  const cidades = useMemo(() => {
    const detalhamento = data.detalhamento?.oss || [];
    return [...new Set(detalhamento.map(item => item.CIDADE).filter(Boolean))].sort();
  }, [data]);

  const tecnicos = useMemo(() => {
    const detalhamento = data.detalhamento?.oss || [];
    return [...new Set(detalhamento.map(item => item.TÉCNICO).filter(Boolean))].sort();
  }, [data]);

  // Listas únicas para OSS não finalizadas
  const cidadesNaoFinalizada = useMemo(() => {
    const naoFinalizada = data.nao_finalizada?.oss || [];
    return [...new Set(naoFinalizada.map(item => item.CIDADE).filter(Boolean))].sort();
  }, [data]);

  const tecnicosNaoFinalizada = useMemo(() => {
    const naoFinalizada = data.nao_finalizada?.oss || [];
    return [...new Set(naoFinalizada.map(item => item.TÉCNICO).filter(Boolean))].sort();
  }, [data]);

  // Filtrar dados de detalhamento
  const dadosFiltrados = useMemo(() => {
    const detalhamento = data.detalhamento?.oss || [];
    return detalhamento.filter(item => {
      const matchBusca = 
        item['ID OS']?.toString().includes(busca.toLowerCase()) ||
        (item.CLIENTE && String(item.CLIENTE).toLowerCase().includes(busca.toLowerCase())) ||
        item.CONTRATO?.toString().includes(busca.toLowerCase()) ||
        (item.ASSUNTO && String(item.ASSUNTO).toLowerCase().includes(busca.toLowerCase()));
      
      const matchCidade = !filtroCidade || item.CIDADE === filtroCidade;
      const matchTecnico = !filtroTecnico || item.TÉCNICO === filtroTecnico;
      
      return matchBusca && matchCidade && matchTecnico;
    });
  }, [data, busca, filtroCidade, filtroTecnico]);

  // Filtrar dados de OSS não finalizadas
  const dadosFiltradosNaoFinalizada = useMemo(() => {
    const naoFinalizada = data.nao_finalizada?.oss || [];
    return naoFinalizada.filter(item => {
      const matchBusca = 
        item['ID OS']?.toString().includes(buscaNaoFinalizada.toLowerCase()) ||
        (item.CLIENTE && String(item.CLIENTE).toLowerCase().includes(buscaNaoFinalizada.toLowerCase())) ||
        item.CONTRATO?.toString().includes(buscaNaoFinalizada.toLowerCase()) ||
        (item.ASSUNTO && String(item.ASSUNTO).toLowerCase().includes(buscaNaoFinalizada.toLowerCase()));
      
      const matchCidade = !filtroCidadeNaoFinalizada || item.CIDADE === filtroCidadeNaoFinalizada;
      const matchTecnico = !filtroTecnicoNaoFinalizada || item.TÉCNICO === filtroTecnicoNaoFinalizada;
      
      return matchBusca && matchCidade && matchTecnico;
    });
  }, [data, buscaNaoFinalizada, filtroCidadeNaoFinalizada, filtroTecnicoNaoFinalizada]);

  const dadosAssunto = useMemo(() => {
    const porAssunto = data.por_assunto?.oss || [];
    return agregarPorCampo(porAssunto, 'ASSUNTO', 'TOTAL', 10, 'ID');
  }, [data, agregarPorCampo]);

  // Colunas para tabela de detalhamento
  const columnsDetalhamento = [
    { key: 'ID OS', label: 'ID OS', sortable: true },
    { key: 'CONTRATO', label: 'Contrato', sortable: true },
    { key: 'CLIENTE', label: 'Cliente', sortable: true },
    { key: 'CIDADE', label: 'Cidade', sortable: true },
    { key: 'BAIRRO', label: 'Bairro', sortable: true },
    { 
      key: 'STATUS DE INTERNET', 
      label: 'Status Internet', 
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'A' ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)]' : 'bg-[color:var(--surface-muted)] text-[color:var(--status-bad)]'
        }`}>
          {value}
        </span>
      )
    },
    { key: 'ABERTURA', label: 'Abertura', sortable: true },
    { key: 'FECHAMENTO', label: 'Fechamento', sortable: true },
    { key: 'DIAGNOSTICO', label: 'Diagnóstico', sortable: true },
    { key: 'ASSUNTO', label: 'Assunto', sortable: true },
    { key: 'TÉCNICO', label: 'Técnico', sortable: true }
  ];

  // Colunas para tabela de não finalizadas
  const columnsNaoFinalizada = [
    { key: 'ID OS', label: 'ID OS', sortable: true },
    { key: 'CONTRATO', label: 'Contrato', sortable: true },
    { key: 'CLIENTE', label: 'Cliente', sortable: true },
    { key: 'CONTATO', label: 'Contato', sortable: true },
    { key: 'CIDADE', label: 'Cidade', sortable: true },
    { key: 'BAIRRO', label: 'Bairro', sortable: true },
    { key: 'ENDEREÇO', label: 'Endereço', sortable: true },
    { key: 'ASSUNTO', label: 'Assunto', sortable: true },
    { 
      key: 'STATUS DE OS', 
      label: 'Status', 
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[color:var(--surface-muted)] text-[color:var(--status-warn)]">
          {value}
        </span>
      )
    },
    { key: 'TÉCNICO', label: 'Técnico', sortable: true },
    { key: 'ABERTURA', label: 'Abertura', sortable: true }
  ];

  if (loading) {
    return (
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-[color:var(--surface-muted)] rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-[color:var(--surface-muted)] rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Total de OSS"
          value={kpisCalculados.totalOSS.toLocaleString('pt-BR')}
          icon={BarChart3}
          color="blue"
        />
        <KPICard
          title="Média por Dia"
          value={kpisCalculados.mediaPorDia.toLocaleString('pt-BR', { minimumFractionDigits: 1 })}
          icon={TrendingUp}
          color="green"
        />
        <KPICard
          title="OSS Não Finalizadas"
          value={kpisCalculados.ossNaoFinalizadas.toLocaleString('pt-BR')}
          icon={AlertTriangle}
          color="red"
        />
        <KPICard
          title="% Não Finalizadas"
          value={`${kpisCalculados.percentualNaoFinalizadas}%`}
          icon={Clock}
          color="orange"
        />
      </div>

      {/* Tabs de Navegação */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)]">
        <div className="border-b border-[color:var(--border)]">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
              { id: 'detalhamento', label: 'Detalhamento', icon: CheckCircle },
              { id: 'nao-finalizada', label: 'Não Finalizadas', icon: AlertTriangle }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-[color:var(--accent)] text-[color:var(--accent)]'
                    : 'border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:border-[color:var(--border)]'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Gráficos de Pizza */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CustomPieChart
                  data={dadosDiagnostico}
                  title="Top 10 - Por Diagnóstico"
                  dataKey="value"
                  nameKey="name"
                  height={300}
                />
                <CustomPieChart
                  data={dadosAssunto}
                  title="Top 10 - Por Assunto"
                  dataKey="value"
                  nameKey="name"
                  height={300}
                />
              </div>

              {/* Gráficos de Tempo (Barras Verticais) */}
              <GraficosTempoDashboard data={data} />

              {/* Componente de Classificação de OSS (Barras Horizontais) */}
              <ClassificacaoOSSDashboard data={data} />
            </div>
          )}

          {activeTab === 'detalhamento' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-[color:var(--text-primary)]">
                  Detalhamento de OSS
                  <span className="ml-2 text-sm text-[color:var(--text-muted)]">
                    ({dadosFiltrados.length} de {(data.detalhamento?.oss || []).length} registros)
                  </span>
                </h3>
                
                {/* Componente de Pesquisa Compacto */}
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-[color:var(--text-muted)]" />
                    </div>
                    <input
                      type="text"
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      placeholder="Buscar OS, cliente, contrato ou assunto..."
                      className="block w-64 pl-9 pr-3 py-2 text-sm border border-[color:var(--border)] rounded-md leading-5 bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                    />
                  </div>
                  
                  <div className="w-48">
                    <SearchableFilterSelect
                      options={cidades}
                      value={filtroCidade}
                      onChange={(value) => setFiltroCidade(value || '')}
                      placeholder="Buscar cidade..."
                      allOptionLabel="Todas as cidades"
                      allOptionValue=""
                    />
                  </div>
                  
                  <div className="w-48">
                    <SearchableFilterSelect
                      options={tecnicos}
                      value={filtroTecnico}
                      onChange={(value) => setFiltroTecnico(value || '')}
                      placeholder="Buscar técnico..."
                      allOptionLabel="Todos os técnicos"
                      allOptionValue=""
                    />
                  </div>
                </div>
              </div>
              
              <DataTable
                data={dadosFiltrados}
                columns={columnsDetalhamento}
                loading={loading}
                searchable={false}
                pagination={true}
                pageSize={20}
              />
            </div>
          )}

          {activeTab === 'nao-finalizada' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-[color:var(--text-primary)]">
                  OSS Não Finalizadas
                  <span className="ml-2 text-sm text-[color:var(--text-muted)]">
                    ({dadosFiltradosNaoFinalizada.length} de {(data.nao_finalizada?.oss || []).length} registros)
                  </span>
                </h3>
                
                {/* Componente de Pesquisa Compacto */}
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-[color:var(--text-muted)]" />
                    </div>
                    <input
                      type="text"
                      value={buscaNaoFinalizada}
                      onChange={(e) => setBuscaNaoFinalizada(e.target.value)}
                      placeholder="Buscar OS, cliente, contrato ou assunto..."
                      className="block w-64 pl-9 pr-3 py-2 text-sm border border-[color:var(--border)] rounded-md leading-5 bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                    />
                  </div>
                  
                  <div className="w-48">
                    <SearchableFilterSelect
                      options={cidadesNaoFinalizada}
                      value={filtroCidadeNaoFinalizada}
                      onChange={(value) => setFiltroCidadeNaoFinalizada(value || '')}
                      placeholder="Buscar cidade..."
                      allOptionLabel="Todas as cidades"
                      allOptionValue=""
                    />
                  </div>
                  
                  <div className="w-48">
                    <SearchableFilterSelect
                      options={tecnicosNaoFinalizada}
                      value={filtroTecnicoNaoFinalizada}
                      onChange={(value) => setFiltroTecnicoNaoFinalizada(value || '')}
                      placeholder="Buscar técnico..."
                      allOptionLabel="Todos os técnicos"
                      allOptionValue=""
                    />
                  </div>
                </div>
              </div>
              
              <DataTable
                data={dadosFiltradosNaoFinalizada}
                columns={columnsNaoFinalizada}
                loading={loading}
                searchable={false}
                pagination={true}
                pageSize={20}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default OSSEstoqueDashboard;
